import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaStar } from 'react-icons/fa';
import {
  FiCheckCircle,
  FiClock,
  FiCornerUpLeft,
  FiEdit3,
  FiLock,
  FiMessageSquare,
  FiSend,
  FiUser,
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { useLocale } from '../context/LocaleContext';
import { resolveSettingMediaUrl } from '../context/SiteSettingsContext';
import { api } from '../services/api';
import './ProductReviews.css';

const EMPTY_SUMMARY = {
  average: 0,
  total: 0,
  distribution: [5, 4, 3, 2, 1].map((rating) => ({ rating, count: 0, percentage: 0 })),
};

const STATUS_INFO = {
  pending: { icon: FiClock, tone: 'pending' },
  approved: { icon: FiCheckCircle, tone: 'approved' },
  rejected: { icon: FiEdit3, tone: 'rejected' },
};

const RatingStars = ({ value, interactive = false, onChange, disabled = false }) => {
  const { t } = useTranslation();
  const [hovered, setHovered] = useState(0);
  const activeValue = interactive && hovered ? hovered : Number(value || 0);

  return (
    <div className={`product-review-stars ${interactive ? 'is-interactive' : ''}`} aria-label={t('customerUi.reviews.ratingLabel', { value: value || 0 })}>
      {[1, 2, 3, 4, 5].map((rating) => (
        interactive ? (
          <button
            key={rating}
            type="button"
            className={rating <= activeValue ? 'is-active' : ''}
            onMouseEnter={() => setHovered(rating)}
            onMouseLeave={() => setHovered(0)}
            onFocus={() => setHovered(rating)}
            onBlur={() => setHovered(0)}
            onClick={() => onChange(rating)}
            aria-label={t('customerUi.reviews.starLabel', { value: rating })}
            aria-pressed={Number(value) === rating}
            disabled={disabled}
          >
            <FaStar />
          </button>
        ) : (
          <FaStar key={rating} className={rating <= activeValue ? 'is-active' : ''} aria-hidden="true" />
        )
      ))}
    </div>
  );
};

const ProductReviews = ({ productId, productSlug, productName, onSummaryChange }) => {
  const { t } = useTranslation();
  const { localePath, formatDate, formatNumber } = useLocale();
  const { user, isAuthenticated, authLoading } = useAuth();
  const [reviews, setReviews] = useState([]);
  const [summary, setSummary] = useState(EMPTY_SUMMARY);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [myReview, setMyReview] = useState(null);
  const [rating, setRating] = useState(0);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [notice, setNotice] = useState(null);
  const highlightedReviewId = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('review')
    : '';

  const fetchReviews = useCallback(async () => {
    if (!productId) return;
    setLoading(true);
    try {
      const query = new URLSearchParams({ page: String(page), limit: '6' });
      if (highlightedReviewId) query.set('highlight', highlightedReviewId);
      const response = await api.get(`/api/product-reviews/product/${productId}?${query.toString()}`);
      const data = response.data || {};
      const nextSummary = data.summary || EMPTY_SUMMARY;
      setReviews(Array.isArray(data.items) ? data.items : []);
      setSummary(nextSummary);
      setPages(Number(data.pages || 1));
      onSummaryChange?.(nextSummary);
    } catch (error) {
      setReviews([]);
      setNotice({ type: 'error', text: error.message || t('customerUi.reviews.loadError') });
    } finally {
      setLoading(false);
    }
  }, [highlightedReviewId, onSummaryChange, page, productId, t]);

  const fetchMyReview = useCallback(async () => {
    if (!productId || !isAuthenticated) {
      setMyReview(null);
      setRating(0);
      setBody('');
      return;
    }
    try {
      const response = await api.get(`/api/product-reviews/mine/${productId}`);
      const review = response.data || null;
      setMyReview(review);
      setRating(Number(review?.rating || 0));
      setBody(review?.body || '');
    } catch (error) {
      setNotice({ type: 'error', text: error.message || t('customerUi.reviews.mineError') });
    }
  }, [isAuthenticated, productId, t]);

  useEffect(() => {
    fetchReviews();
  }, [fetchReviews]);

  useEffect(() => {
    if (!authLoading) fetchMyReview();
  }, [authLoading, fetchMyReview]);

  useEffect(() => {
    if (!highlightedReviewId || !reviews.length || typeof document === 'undefined') return;
    const target = document.getElementById(`review-${highlightedReviewId}`);
    if (!target) return;
    target.scrollIntoView({ block: 'center' });
    target.focus({ preventScroll: true });
  }, [highlightedReviewId, reviews]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setNotice(null);
    if (!rating) {
      setNotice({ type: 'error', text: t('customerUi.reviews.chooseRating') });
      return;
    }
    if (body.trim().length < 10) {
      setNotice({ type: 'error', text: t('customerUi.reviews.bodyShort') });
      return;
    }

    setSubmitting(true);
    try {
      const response = await api.post(`/api/product-reviews/product/${productId}`, {
        rating,
        body: body.trim(),
      });
      setMyReview(response.data);
      setNotice({ type: 'success', text: response.message || t('customerUi.reviews.saved') });
      await fetchReviews();
    } catch (error) {
      setNotice({ type: 'error', text: error.data?.message || error.message || t('customerUi.reviews.saveError') });
    } finally {
      setSubmitting(false);
    }
  };

  const returnPath = localePath(`/products/${productSlug || productId}?tab=reviews`);
  const statusInfo = myReview ? STATUS_INFO[myReview.status] || STATUS_INFO.pending : null;
  const StatusIcon = statusInfo?.icon;

  return (
    <div className="product-reviews">
      <header className="product-reviews-heading">
        <div>
          <span><FiMessageSquare /></span>
          <div>
            <h3>{t('customerUi.reviews.title')}</h3>
            <p>{t('customerUi.reviews.subtitle', { product: productName || t('customerUi.reviews.thisProduct') })}</p>
          </div>
        </div>
        <span className="product-reviews-count">{t('customerUi.reviews.approvedCount', { count: formatNumber(summary.total) })}</span>
      </header>

      <div className="product-reviews-top-grid">
        <section className="product-reviews-summary" aria-label={t('customerUi.reviews.summaryLabel')}>
          <div className="product-reviews-score">
            <strong>{summary.total ? formatNumber(summary.average) : '—'}</strong>
            <RatingStars value={Math.round(summary.average)} />
            <span>{summary.total ? t('customerUi.reviews.totalReviews', { count: formatNumber(summary.total) }) : t('customerUi.reviews.noRating')}</span>
          </div>
          <div className="product-reviews-bars">
            {summary.distribution.map((item) => (
              <div key={item.rating}>
                <span>{formatNumber(item.rating)} <FaStar /></span>
                <i><b style={{ width: `${item.percentage}%` }} /></i>
                <small>{formatNumber(item.count)}</small>
              </div>
            ))}
          </div>
        </section>

        <section className="product-review-compose">
          {authLoading ? (
            <div className="product-review-form-loading">{t('customerUi.reviews.checkingAccount')}</div>
          ) : !isAuthenticated ? (
            <div className="product-review-auth-card">
              <span><FiLock /></span>
              <div>
                <strong>{t('customerUi.reviews.loginTitle')}</strong>
                <p>{t('customerUi.reviews.loginHint')}</p>
              </div>
              <div>
                <Link to={localePath('/login')} state={{ from: returnPath }}>{t('customerUi.reviews.login')}</Link>
                <Link to={localePath('/register')} state={{ from: returnPath }} className="is-secondary">{t('customerUi.reviews.register')}</Link>
              </div>
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="product-review-compose-head">
                <div className="product-review-current-user">
                  <span>
                    {user?.avatar
                      ? <img src={resolveSettingMediaUrl(user.avatar)} alt="" />
                      : (user?.name || t('customerUi.reviews.user')).charAt(0)}
                  </span>
                  <div><strong>{user?.name || t('customerUi.reviews.user')}</strong><small>{myReview ? t('customerUi.reviews.editExperience') : t('customerUi.reviews.writeExperience')}</small></div>
                </div>
                {statusInfo && (
                  <span className={`product-review-own-status is-${statusInfo.tone}`}><StatusIcon /> {t(`customerUi.reviews.statuses.${myReview.status}`)}</span>
                )}
              </div>

              {myReview?.status === 'rejected' && myReview.rejectionReason && (
                <div className="product-review-rejection-note"><strong>{t('customerUi.reviews.adminNote')}</strong> {myReview.rejectionReason}</div>
              )}

              <label className="product-review-rating-field">
                <span>{t('customerUi.reviews.yourRating')}</span>
                <RatingStars value={rating} interactive onChange={setRating} disabled={submitting} />
              </label>
              <label className="product-review-text-field">
                <span>{t('customerUi.reviews.reviewText')}</span>
                <textarea
                  value={body}
                  onChange={(event) => setBody(event.target.value.slice(0, 2000))}
                  placeholder={t('customerUi.reviews.placeholder')}
                  rows="5"
                  maxLength="2000"
                  disabled={submitting}
                />
                <small>{formatNumber(body.length)} / {formatNumber(2000)}</small>
              </label>
              <footer>
                <p><FiClock /> {t('customerUi.reviews.moderation')}</p>
                <button type="submit" disabled={submitting || !rating || body.trim().length < 10}>
                  <FiSend /> {submitting ? t('customerUi.reviews.submitting') : myReview ? t('customerUi.reviews.update') : t('customerUi.reviews.submit')}
                </button>
              </footer>
            </form>
          )}
        </section>
      </div>

      {notice?.text && <div className={`product-review-notice is-${notice.type}`} role={notice.type === 'error' ? 'alert' : 'status'}>{notice.text}</div>}

      <section className="product-review-feed" aria-label={t('customerUi.reviews.approvedFeed')}>
        {loading ? (
          <div className="product-reviews-empty">{t('customerUi.reviews.loading')}</div>
        ) : reviews.length === 0 ? (
          <div className="product-reviews-empty">
            <FiMessageSquare />
            <strong>{t('customerUi.reviews.empty')}</strong>
            <span>{t('customerUi.reviews.emptyHint')}</span>
          </div>
        ) : (
          reviews.map((review) => (
            <article
              key={review._id}
              id={`review-${review._id}`}
              className={`product-review-card ${highlightedReviewId === String(review._id) ? 'is-highlighted' : ''}`}
              tabIndex={-1}
            >
              <header>
                <div className="product-review-author-avatar">
                  {review.author?.avatar
                    ? <img src={resolveSettingMediaUrl(review.author.avatar)} alt="" />
                    : <FiUser />}
                </div>
                <div className="product-review-author-copy">
                  <strong>{review.author?.name || t('customerUi.reviews.user')}</strong>
                  <time>{formatDate(review.createdAt, { year: 'numeric', month: 'long', day: 'numeric' })}</time>
                </div>
                <RatingStars value={review.rating} />
              </header>
              <p>{review.body}</p>
              {review.reply?.body && (
                <section className="product-review-official-reply">
                  <header>
                    <span><FiCornerUpLeft /></span>
                    <div>
                      <strong>{t('customerUi.reviews.reply')}</strong>
                      <time>{formatDate(review.reply.updatedAt || review.reply.createdAt, { year: 'numeric', month: 'long', day: 'numeric' })}</time>
                    </div>
                  </header>
                  <p>{review.reply.body}</p>
                </section>
              )}
            </article>
          ))
        )}

        {pages > 1 && (
          <div className="product-reviews-pagination">
            <button type="button" disabled={page <= 1} onClick={() => setPage((value) => value - 1)}>{t('customerUi.common.previous')}</button>
            <span>{t('customerUi.common.page', { page: formatNumber(page), pages: formatNumber(pages) })}</span>
            <button type="button" disabled={page >= pages} onClick={() => setPage((value) => value + 1)}>{t('customerUi.common.next')}</button>
          </div>
        )}
      </section>
    </div>
  );
};

export default ProductReviews;
